import React, { useState } from 'react';
import './PriceRuleForm.css';
import '../../../shared/StyledInput.css';
import RequiredMark from '../../../shared/RequiredMark';
import CancelButton from '../../../shared/CancelButton';
import SaveButton from '../../../shared/SaveButton';
import { useAnchorPriceGroupName } from '../../../shared/useAnchorPriceGroupName';
import NumberInput from '../../../shared/NumberInput';

const TYPE_OPTIONS = [
  { value: 'percent_off', label: 'Percent Off' },
  { value: 'amount_off', label: 'Amount Off' },
];

const ROUNDING_INCREMENT_OPTIONS = [
  { value: 1, label: '1 Cent' },
  { value: 5, label: '5 Cents' },
  { value: 10, label: '10 Cents' },
];

const ROUNDING_DIRECTION_OPTIONS = [
  { value: 'nearest', label: 'Nearest' },
  { value: 'up', label: 'Up' },
  { value: 'down', label: 'Down' },
];

// Base Price x the UoM's quantity, or Retail's own price for the UoM. The
// two are the same figure while every UoM's Retail Price is calculated from
// the Base Price; Base Price is the default so a Rule never follows a Retail
// pack/box discount unless someone chose that (see priceResolution.js's
// ruleBasisPrice).
function basisOptions(anchorName) {
  return [
    { value: 'base_price', label: 'Base Price' },
    { value: 'retail_price', label: `${anchorName} Price` },
  ];
}

function amountLabel(type) {
  return type === 'percent_off' ? 'Percent (%)' : 'Amount Off ($)';
}

function emptyTierRow() {
  return { min_quantity: '', amount: '' };
}

function initialTierRows(initial) {
  if (initial?.tiers?.length) {
    return initial.tiers.map((tier) => ({
      min_quantity: String(Number(tier.min_quantity)),
      amount: Number(tier.amount).toFixed(2),
    }));
  }
  return [emptyTierRow()];
}

function PriceRuleForm({ initial, onSave, onCancel, loading }) {
  const anchorName = useAnchorPriceGroupName();
  const [name, setName] = useState(initial?.name || '');
  const [description, setDescription] = useState(initial?.description || '');
  const [basis, setBasis] = useState(initial?.basis || 'base_price');
  const [type, setType] = useState(initial?.type || 'percent_off');
  const [roundingIncrement, setRoundingIncrement] = useState(initial?.rounding_increment ?? 1);
  const [roundingDirection, setRoundingDirection] = useState(initial?.rounding_direction || 'nearest');
  const [tiers, setTiers] = useState(initialTierRows(initial));
  const [error, setError] = useState('');

  const updateTier = (index, field, value) => {
    setTiers((rows) => rows.map((row, i) => (i === index ? { ...row, [field]: value } : row)));
  };

  const addTierRow = () => {
    setTiers((rows) => [...rows, emptyTierRow()]);
  };

  const removeTierRow = (index) => {
    setTiers((rows) => rows.filter((_, i) => i !== index));
  };

  const validate = (parsedTiers) => {
    if (!name.trim()) return 'Rule name is required.';
    if (parsedTiers.length === 0) return 'At least one tier is required.';

    const seenQuantities = new Set();
    for (const tier of parsedTiers) {
      if (Number.isNaN(tier.min_quantity) || tier.min_quantity <= 0) {
        return 'Each tier must have a positive minimum quantity.';
      }
      if (Number.isNaN(tier.amount)) {
        return 'Each tier must have an amount.';
      }
      if (seenQuantities.has(tier.min_quantity)) {
        return 'Tier minimum quantities must be unique.';
      }
      seenQuantities.add(tier.min_quantity);
    }
    return '';
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const parsedTiers = tiers
      .filter((row) => row.min_quantity !== '' || row.amount !== '')
      .map((row) => ({ min_quantity: Number(row.min_quantity), amount: Number(row.amount) }));

    const err = validate(parsedTiers);
    if (err) return setError(err);
    setError('');

    onSave({
      name: name.trim(),
      description: description.trim(),
      basis,
      type,
      rounding_increment: Number(roundingIncrement),
      rounding_direction: roundingDirection,
      tiers: parsedTiers
        .sort((a, b) => a.min_quantity - b.min_quantity)
        .map((tier) => ({ min_quantity: tier.min_quantity, amount: Number(tier.amount.toFixed(2)) })),
    });
  };

  return (
    <form className="price-rule-form" onSubmit={handleSubmit} style={{ minWidth: 320 }}>
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
        <div>
          <label>Rule Name<RequiredMark /></label><br />
          <input
            className="styled-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={100}
            placeholder={`e.g., 10% off ${anchorName} Price`}
          />
        </div>
        <div>
          <label>Basis</label><br />
          <select
            className="styled-input"
            value={basis}
            onChange={(e) => setBasis(e.target.value)}
            title={basis === 'base_price'
              ? `Calculates from the Base Price x the UoM's quantity`
              : `Calculates from ${anchorName}'s own price for the UoM`}
          >
            {basisOptions(anchorName).map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label>Type<RequiredMark /></label><br />
          <select className="styled-input" value={type} onChange={(e) => setType(e.target.value)}>
            {TYPE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label>Round To<RequiredMark /></label><br />
          <select className="styled-input" value={roundingIncrement} onChange={(e) => setRoundingIncrement(Number(e.target.value))}>
            {ROUNDING_INCREMENT_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label>Round Direction<RequiredMark /></label><br />
          <select className="styled-input" value={roundingDirection} onChange={(e) => setRoundingDirection(e.target.value)}>
            {ROUNDING_DIRECTION_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
      </div>

      <div style={{ marginTop: 14 }}>
        <label>Description</label><br />
        <textarea
          className="styled-input"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={2}
          style={{ width: '100%', maxWidth: 480, resize: 'vertical' }}
          placeholder="Optional notes about when/why this rule is used"
        />
      </div>

      <div style={{ marginTop: 14 }}>
        <label>Quantity Tiers<RequiredMark /></label>
        <div style={{ fontSize: 12, color: '#888', marginTop: 2, marginBottom: 4 }}>
          Min Qty is always in the product's Base UoM (e.g. Pieces) — this Rule may be reused by Prices in different UoMs, so a tier can't be entered in Packs or Boxes.
        </div>
        <table style={{ borderCollapse: 'collapse', marginTop: 6 }}>
          <thead>
            <tr style={{ textAlign: 'left', color: '#666', fontSize: 13 }}>
              <th style={{ fontWeight: 500, padding: '0 8px 4px 0' }}>Min Qty (Base UoM)</th>
              <th style={{ fontWeight: 500, padding: '0 8px 4px 0' }}>{amountLabel(type)}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {tiers.map((row, index) => (
              <tr key={index}>
                <td style={{ padding: '2px 8px 2px 0' }}>
                  <NumberInput
                    maxDecimals={4}
                    step="0.0001"
                    min="0"
                    value={row.min_quantity}
                    onChange={(e) => updateTier(index, 'min_quantity', e.target.value)}
                    style={{ width: 80 }}
                  />
                </td>
                <td style={{ padding: '2px 8px 2px 0' }}>
                  <NumberInput
                    decimals={2}
                    step="0.01"
                    value={row.amount}
                    onChange={(e) => updateTier(index, 'amount', e.target.value)}
                    style={{ width: 90 }}
                  />
                </td>
                <td style={{ padding: '2px 0' }}>
                  <button
                    type="button"
                    onClick={() => removeTierRow(index)}
                    disabled={tiers.length <= 1}
                    title={tiers.length <= 1 ? 'At least one tier is required' : 'Remove tier'}
                    style={{ padding: '2px 8px' }}
                  >
                    Remove
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <button type="button" onClick={addTierRow} style={{ marginTop: 6, padding: '4px 10px' }}>
          + Add tier
        </button>
      </div>

      {error && <div style={{ color: 'red', marginTop: 8 }}>{error}</div>}

      <div style={{ marginTop: 12 }}>
        <SaveButton loading={loading} />
        <CancelButton onClick={onCancel} disabled={loading} style={{ marginLeft: 8 }} />
      </div>
    </form>
  );
}

export default PriceRuleForm;
